import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { historyService } from '../services/historyService';

interface ExplorationItem {
  id: string;
  created_at: string;
  data: {
    query: string;
    snapshot: {
      intent?: {
        type: string;
      };
    };
  };
}

interface RightSidebarProps {
  userId: string;
  onExplore: (query: string) => void;
  onLoadExploration: (id: string) => void;
  trigger?: number;
  isOpen?: boolean;
  onClose?: () => void;
  onBackToLeft?: () => void;
  hasHeaderSearch?: boolean;
}

const SidebarContainer = styled.div<{ isOpen?: boolean; hasHeaderSearch?: boolean }>`
  width: 320px;
  height: calc(100vh - 60px);
  background: rgba(var(--color-surface-rgb, 30, 41, 59), 0.85);
  backdrop-filter: blur(12px);
  border-left: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: fixed;
  right: 0;
  top: 60px;
  z-index: 20;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  
  @media (max-width: 1024px) {
    /* On mobile, slide from left instead of right */
    left: 0;
    right: auto;
    border-left: none;
    border-right: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
    top: ${props => props.hasHeaderSearch ? '140px' : '60px'};
    height: ${props => props.hasHeaderSearch ? 'calc(100vh - 140px)' : 'calc(100vh - 60px)'};
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SidebarHeader = styled.div`
  padding: var(--gap-lg);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileCloseButton = styled.button`
  display: none;
  width: 32px;
  height: 32px;
  background: var(--color-surface-hover);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 1024px) {
    display: flex;
  }
  
  &:hover {
    background: var(--color-primary);
    color: white;
    transform: translateY(-1px);
  }
  
  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const SidebarTitleContainer = styled.div`
  flex: 1;
`;

const SidebarTitle = styled.h3`
  font-family: var(--font-body);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--gap-sm);
`;

const SubTitle = styled.p`
  font-size: var(--font-size-sm);
  color: var(--color-text);
  opacity: 0.7;
  margin: var(--gap-xs) 0 0 0;
  line-height: 1.3;
`;

const TimelineContainer = styled.div`
  padding: var(--gap-lg);
  flex: 1;
`;

const Timeline = styled.div`
  position: relative;
  
  /* Vertical line */
  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, var(--color-primary), transparent);
    opacity: 0.3;
  }
`;

const TimelineItem = styled.button<{ $isCurrent?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: var(--gap-lg);
  text-align: left;
  width: 100%;
  position: relative;
  display: flex;
  align-items: flex-start;
  
  /* Timeline dot */
  &::before {
    content: '';
    position: absolute;
    left: 14px;
    top: 8px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--color-border);
    border: 2px solid var(--color-surface);
    z-index: 10;
    transition: all 0.3s ease;
  }
  
  ${props => props.$isCurrent && `
    &::before {
      background: var(--color-success);
      box-shadow: 0 0 0 4px var(--color-success-light, rgba(16, 185, 129, 0.2));
      transform: scale(1.2);
    }
  `}
  
  &:hover {
    &::before {
      background: var(--color-primary);
      transform: scale(1.3);
    }
  }
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--gap-xs);
  min-width: 0;
  margin-left: 32px;
`;

const ItemText = styled.span`
  font-family: var(--font-body);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  line-height: 1.4;
  word-wrap: break-word;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--gap-sm);
  font-size: var(--font-size-xs);
  color: var(--color-text);
  opacity: 0.7;
  flex-wrap: wrap;
`;

const TimeStamp = styled.span`
  background: var(--color-border);
  padding: 2px 6px;
  border-radius: var(--radius-xs);
  font-weight: var(--font-weight-medium);
`;

const InteractionBadge = styled.span`
  background: linear-gradient(135deg, var(--color-success-light, rgba(16, 185, 129, 0.2)), var(--color-success-lighter, rgba(16, 185, 129, 0.1)));
  border: 1px solid var(--color-success-border, rgba(16, 185, 129, 0.4));
  color: var(--color-success);
  padding: 2px 6px;
  border-radius: var(--radius-xs);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-2xs);
`;

const StatsSection = styled.div`
  padding: var(--gap-lg);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--gap-md);
`;

const StatCard = styled.div`
  text-align: center;
  padding: var(--gap-sm);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
`;

const StatValue = styled.div`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  margin-bottom: var(--gap-xs);
`;

const StatLabel = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text);
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gap-xl);
  color: var(--color-text);
  opacity: 0.7;
  font-size: var(--font-size-sm);
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--color-text);
  opacity: 0.7;
  font-size: var(--font-size-sm);
  padding: var(--gap-xl);
  line-height: 1.5;
`;

const MobileNavSection = styled.div`
  display: none;
  
  @media (max-width: 1024px) {
    display: block;
    padding: var(--gap-lg);
    border-bottom: 1px solid var(--color-border);
    background: var(--color-background);
  }
`;

const MobileNavGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--gap-md);
`;

const MobileNavButton = styled.button`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--gap-lg);
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gap-sm);
  min-height: 80px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    opacity: 0.8;
  }
`;

const RightSidebar: React.FC<RightSidebarProps> = ({ userId, onExplore, onLoadExploration, trigger, isOpen = false, onClose, onBackToLeft, hasHeaderSearch = false }) => {
  const [path, setPath] = useState<ExplorationItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPath = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const data = await historyService.getUserHistory(15); // Show more items for timeline
      setPath(data); // Already sorted by most recent first
    } catch (error) {
      console.error('Error fetching exploration path:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPath();
  }, [userId, trigger]);

  const handleItemClick = (exploration: ExplorationItem) => {
    onLoadExploration(exploration.id);
  };


  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  };

  const calculateDepth = (): string => {
    if (path.length === 0) return 'surface';
    if (path.length < 3) return 'surface';
    if (path.length < 8) return 'medium';
    return 'deep';
  };


  const currentDepth = calculateDepth();
  const uniqueTopics = new Set(path.map(p => p.data.query)).size;
  const totalInteractions = path.length; // Each exploration is one interaction

  return (
    <SidebarContainer isOpen={isOpen} hasHeaderSearch={hasHeaderSearch}>
      <SidebarHeader>
        <SidebarTitleContainer>
          <SidebarTitle>
            Your Journey
          </SidebarTitle>
          <SubTitle>
            Your exploration timeline and progress
          </SubTitle>
        </SidebarTitleContainer>
        <MobileCloseButton onClick={onBackToLeft || onClose} title="Back">
          <svg viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </MobileCloseButton>
      </SidebarHeader>


      <TimelineContainer>
        {loading && path.length === 0 ? (
          <LoadingState>
            Loading your journey...
          </LoadingState>
        ) : path.length === 0 ? (
          <EmptyState>
            Your exploration timeline will appear here as you discover topics!
            <br /><br />
            Start exploring to build your journey.
          </EmptyState>
        ) : (
          <Timeline>
            {path.map((exploration, index) => (
              <TimelineItem
                key={exploration.id}
                onClick={() => handleItemClick(exploration)}
                $isCurrent={index === 0} // Most recent is first
              >
                <ItemContent>
                  <ItemText>{exploration.data.query}</ItemText>
                  <ItemMeta>
                    <TimeStamp>
                      {formatTimeAgo(exploration.created_at)}
                    </TimeStamp>
                    {exploration.data.snapshot.intent?.type && (
                      <InteractionBadge>
                        {exploration.data.snapshot.intent.type.toLowerCase()}
                      </InteractionBadge>
                    )}
                  </ItemMeta>
                </ItemContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </TimelineContainer>

      {path.length > 0 && (
        <StatsSection>
          <StatsGrid>
            <StatCard>
              <StatValue>{currentDepth}</StatValue>
              <StatLabel>Depth</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{uniqueTopics}</StatValue>
              <StatLabel>Queries</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{totalInteractions}</StatValue>
              <StatLabel>Interactions</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{path.length}</StatValue>
              <StatLabel>Steps</StatLabel>
            </StatCard>
          </StatsGrid>
        </StatsSection>
      )}
    </SidebarContainer>
  );
};

export default RightSidebar;